import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import { Panel, PanelGroup, PanelResizeHandle, getPanelElement, getPanelGroupElement, getResizeHandleElement } from 'react-resizable-panels';
import styled from 'styled-components';

import { Panel as LayoutPanel } from '../Panel';
import { AutoPanelProvider } from './AutoPanelProvider';
import { DebugPanel } from './components/DebugPanel';
import { SmartModal } from './components/SmartModal';
import { DEFAULT_PANEL_CONFIG } from './constants';
import { LayoutStrategyProvider, useLayoutStrategy } from './contexts/LayoutStrategyContext';
import { ViewportSizeProvider, useViewportSize } from './contexts/ViewportSizeContext';
import { useAutoLayout } from './hooks/useAutoLayout';
import { useAdvancedAutoLayout } from './hooks/useAutoLayoutStrategy';
import { useResponsiveMode } from './hooks/useResponsive';
import { AutoPanelLayoutProps, LayoutArea, ResponsiveMode, SizeConstraint } from './types';
import { useComponentCache } from './utils/componentCache';
import { isValidContent } from './utils/contentDetection';
import { ViewportInfo, pixelsToPercent, sizeConstraintToPixels, usePixelAwarePanelSizing } from './utils/dynamicSizing';
import { LayoutPattern } from './utils/layoutStrategies';

// Imperative API interface for programmatic panel control
export interface AutoPanelLayoutHandle {
  // Get panel elements for advanced manipulation
  getPanelElement: (area: LayoutArea) => HTMLElement | null;
  getPanelGroupElement: () => HTMLElement | null;
  getResizeHandleElement: (handleId: string) => HTMLElement | null;

  // Programmatic panel operations
  collapsePanels: (areas: LayoutArea[]) => void;
  expandPanels: (areas: LayoutArea[]) => void;
  resetLayout: () => void;

  // Layout state tracking
  getCurrentLayout: () => number[];
  getVisibleAreas: () => LayoutArea[];
}

// Styled components for the unified layout
const StyledTabbedLayout = styled.div<{
  $isMobile: boolean;
  $hasLeftSidebar: boolean;
  $hasRightSidebar: boolean;
  $sidebarMode: 'overlay' | 'stack' | 'hidden';
  $compactSpacing?: boolean;
  $noSpacing?: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: 100%; /* Use full available height from parent */
  width: 100%; /* Use full available width from parent */
  overflow: hidden;

  ${({ $compactSpacing, theme }) => $compactSpacing && `gap: ${theme.space.spacing10};`}
  ${({ $noSpacing }) => $noSpacing && 'gap: 0;'}

  ${({ $isMobile, $sidebarMode }) => {
    if ($isMobile && $sidebarMode === 'overlay') {
      return `
        position: relative;
        z-index: 1;
      `;
    }
    return '';
  }}
`;

const StyledResizableLayout = styled.div`
  height: 100%; /* Use full available height from parent */
  width: 100%; /* Use full available width from parent */
  overflow: hidden;
`;

const StyledPanelGroup = styled(PanelGroup)`
  height: 100%;
  width: 100%;
`;

// Helper function to determine panel variant and props based on layout area
const getPanelProps = (area: LayoutArea) => {
  let variant: 'default' | 'header' | 'sidebar' | 'content' | 'footer' = 'default';
  let padding: 'none' | 'compact' | 'default' | 'large' = 'default';

  if (area === LayoutArea.HEADER) {
    variant = 'header';
    padding = 'default';
  } else if (area === LayoutArea.FOOTER) {
    variant = 'footer';
    padding = 'compact';
  } else if (area === LayoutArea.LEFT_SIDEBAR || area === LayoutArea.RIGHT_SIDEBAR) {
    variant = 'sidebar';
    padding = 'compact'; // Reduce sidebar padding for better space utilization
  } else if (area === LayoutArea.CONTENT) {
    variant = 'content';
    padding = 'default'; // Reduce content padding to optimize space
  }

  return { variant, padding, border: true };
};

// Helper function to convert SizeConstraint to number for react-resizable-panels
const constraintToNumber = (
  constraint: number | SizeConstraint | undefined,
  viewport: ViewportInfo,
  direction: 'horizontal' | 'vertical',
  fallback = 0,
): number => {
  if (typeof constraint === 'number') {
    return constraint;
  }
  if (constraint && typeof constraint === 'object') {
    const totalPixels = direction === 'horizontal' ? viewport.width : viewport.height;
    const pixels = sizeConstraintToPixels(constraint, totalPixels);
    return pixelsToPercent(pixels, totalPixels);
  }
  return fallback;
};

// No wrapper needed - let react-resizable-panels handle everything

// Styled wrapper for action buttons layout
const ActionButtonsWrapper = styled(LayoutPanel)`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing20};
  align-items: center;
  justify-content: flex-end;
`;

// Header for collapsible panels (replaces tab headers)
const CollapsiblePanelHeader = styled.div`
  background: ${({ theme }) => theme.colors.palette.grayscale[1]};
  padding: ${({ theme }) => theme.space.spacing20};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  font-size: ${({ theme }) => theme.fontSizes.body};
  color: ${({ theme }) => theme.colors.textPrimary};
  flex-shrink: 0;
`;

// Debug toggle button (floating)
const DebugToggleButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.palette.blues[6]};
  color: white;
  border: none;
  cursor: pointer;
  font-size: 18px;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.palette.blues[7]};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

/**
 * AutoPanelLayout - Single unified layout solution with smart responsive behavior
 *
 * Automatically handles:
 * - Viewport size detection and responsive layout decisions
 * - Panel-to-tab conversion when space is constrained
 * - Modal fallback for content that can't fit
 * - Debug panel integration with fallback strategies
 * - react-resizable-panels optimization with fixed heights
 * - Imperative API for programmatic control
 * - Layout persistence and size tracking
 *
 * @example
 * // Complete layout solution - just wrap your content
 * <AutoPanelLayout autoSaveId="my-layout" onLayout={sizes => console.log(sizes)}>
 *   <MyContent />
 * </AutoPanelLayout>
 *
 * @example
 * // Imperative API for programmatic control
 * function MyComponent() {
 *   const layoutRef = useRef<AutoPanelLayoutHandle>();
 *
 *   const resetPanels = () => {
 *     layoutRef.current?.resetLayout();
 *   };
 *
 *   return <AutoPanelLayout ref={layoutRef} autoSaveId="controlled-layout" />;
 * }
 */
export const AutoPanelLayout = forwardRef<AutoPanelLayoutHandle, AutoPanelLayoutProps>((props, ref) => {
  return (
    <ViewportSizeProvider>
      <LayoutStrategyProvider>
        <AutoPanelProvider onPanelChange={props.onPanelChange}>
          <AutoPanelLayoutCore {...props} ref={ref} />
          <SmartModal />
        </AutoPanelProvider>
      </LayoutStrategyProvider>
    </ViewportSizeProvider>
  );
});

AutoPanelLayout.displayName = 'AutoPanelLayout';

// Core layout component with full integration
const AutoPanelLayoutCore = forwardRef<AutoPanelLayoutHandle, AutoPanelLayoutProps>(
  (
    {
      children,
      autoSaveId,
      direction = 'horizontal',
      className,
      panelConfig,
      // Callback props
      onLayout,
      // Tab-based layout props
      tabs,
      secondaryTabs,
      actionButtons,
      header,
      breadcrumb,
      subheader,
      footer,
      mobileConfig,
      layoutConfig,

      // Auto-layout props
      enablePixelSizing,
      responsive,
      resizeHandlers,
    },
    ref,
  ) => {
    const { panelStates, activePanels, setPanel } = useAutoLayout();
    const responsiveMode = useResponsiveMode();
    const { viewport } = useViewportSize();
    const { recalculateStrategy, strategy } = useLayoutStrategy();
    const _componentCache = useComponentCache();

    // Debug panel state
    const [isDebugPanelVisible, setIsDebugPanelVisible] = React.useState(false);

    // Refs for imperative API
    const mainGroupId = useRef(`${autoSaveId ?? 'auto-panel'}-main`);
    const collapsibleGroupId = useRef(`${autoSaveId ?? 'auto-panel'}-collapsible`);
    const currentLayoutRef = useRef<number[]>([]);

    // Merge panel configurations first
    const finalPanelConfig = useMemo(
      () => ({
        ...DEFAULT_PANEL_CONFIG,
        ...panelConfig,
      }),
      [panelConfig],
    );

    // Get visible areas for sizing calculations
    const filteredVisibleAreas = Array.from(activePanels).filter((area) => {
      const state = panelStates.get(area);
      return state?.isVisible && isValidContent(state.content);
    });

    // Memoize auto-layout config to prevent infinite re-renders
    const autoLayoutConfig = useMemo(
      () => ({
        preferFullHeightSidebars: enablePixelSizing,
        responsiveOverrides: responsive && {
          mobile: LayoutPattern.VERTICAL_STACK,
          tablet: LayoutPattern.HEADER_HORIZONTAL_FOOTER,
          desktop: LayoutPattern.SIDEBAR_VERTICAL_MAIN,
        },
      }),
      [enablePixelSizing, responsive],
    );

    // Use intelligent auto layout strategy for common layout patterns
    const autoLayout = useAdvancedAutoLayout(filteredVisibleAreas, finalPanelConfig, autoLayoutConfig);

    // Use enhanced pixel-aware sizing for layout groups
    const {
      handleLayoutChange: handleMainLayoutChange,
      getPixelAwareLayout: getMainRecommendedLayout,
      getConstraints: getMainConstraints,
    } = usePixelAwarePanelSizing(mainGroupId.current, filteredVisibleAreas, finalPanelConfig, direction, resizeHandlers);

    // Enhanced onLayout callback with state tracking
    const handleLayoutChange = useCallback(
      (sizes: number[]) => {
        currentLayoutRef.current = sizes;
        if (onLayout) {
          onLayout(sizes);
        }
      },
      [onLayout],
    );

    // Implement imperative API
    useImperativeHandle(
      ref,
      () => ({
        getPanelElement: (area: LayoutArea) => getPanelElement(area),
        getPanelGroupElement: () => getPanelGroupElement(mainGroupId.current),
        getResizeHandleElement: (handleId: string) => getResizeHandleElement(handleId),

        collapsePanels: (_areas: LayoutArea[]) => {
          // Implementation would require extending react-resizable-panels API
          console.warn('collapsePanels: Feature requires react-resizable-panels imperative API extension');
        },

        expandPanels: (_areas: LayoutArea[]) => {
          // Implementation would require extending react-resizable-panels API
          console.warn('expandPanels: Feature requires react-resizable-panels imperative API extension');
        },

        resetLayout: () => {
          // Reset would require clearing localStorage for autoSaveId
          if (autoSaveId && typeof window !== 'undefined') {
            window.localStorage.removeItem(`react-resizable-panels:layout:${autoSaveId}`);
            // Simple reset by reload
            window.location.reload();
          }
        },

        getCurrentLayout: () => currentLayoutRef.current,
        getVisibleAreas: () =>
          activePanels.filter((area) => {
            const state = panelStates.get(area);
            return state?.isVisible && isValidContent(state.content);
          }),
      }),
      [autoSaveId, activePanels, panelStates],
    );

    // Simplified layout - no complex grid areas needed

    // Set up core content areas - sub-components like tabs, breadcrumbs should be handled within these areas
    React.useEffect(() => {
      if (header) setPanel(LayoutArea.HEADER, header);
      if (footer) setPanel(LayoutArea.FOOTER, footer);
      // Deprecated: tabs, breadcrumb, subheader, actionButtons should be sub-components of header
      // if (breadcrumb) setPanel(LayoutArea.BREADCRUMB, breadcrumb);
      // if (subheader) setPanel(LayoutArea.SUBHEADER, subheader);
      // if (tabs) setPanel(LayoutArea.TABS, tabs);
      // if (secondaryTabs) setPanel(LayoutArea.SECONDARY_TABS, secondaryTabs);
      // if (actionButtons) setPanel(LayoutArea.ACTION_BUTTONS, actionButtons);
    }, [header, footer, setPanel]);

    // Recalculate strategy when panels change
    React.useEffect(() => {
      const candidatePanels = activePanels.filter((area) => {
        const state = panelStates.get(area);
        return state?.isVisible && isValidContent(state.content);
      });

      // Only recalculate if there are changes
      if (candidatePanels.length > 0) {
        recalculateStrategy(candidatePanels);
      }
    }, [activePanels, panelStates, recalculateStrategy]);

    // Debug panel keyboard shortcut
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.key === 'D') {
          event.preventDefault();
          setIsDebugPanelVisible((prev) => !prev);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Simplified: determine which of the 5 core areas are visible for inline display
    const inlineVisibleAreas = useMemo(() => {
      return activePanels.filter((area) => {
        const state = panelStates.get(area);
        return state?.isVisible && isValidContent(state.content) && strategy.canShowInline(area);
      });
    }, [activePanels, panelStates, strategy]);

    // Determine responsive settings
    const { isMobile, sidebarMode } = useMemo(() => {
      const mode = responsiveMode;
      return {
        isMobile: mode === ResponsiveMode.MOBILE,
        sidebarMode: mobileConfig?.sidebarMode ?? (viewport.recommendedLayout === 'modal-only' ? 'hidden' : 'overlay'),
      };
    }, [responsiveMode, mobileConfig?.sidebarMode, viewport.recommendedLayout]);

    // Render panel content with smart fallback
    const renderPanelContent = useCallback(
      (area: LayoutArea) => {
        const state = panelStates.get(area);
        if (!state?.content || !state.isVisible) return null;

        // Check if content should be displayed as modal
        if (strategy.shouldUseModal(area)) {
          return null; // Content will be handled by SmartModal
        }

        const panelProps = getPanelProps(area);

        // Direct rendering - let react-resizable-panels and Panel component handle all sizing
        return (
          <LayoutPanel {...panelProps} key={`${area}-${area}`}>
            {state.content}
          </LayoutPanel>
        );
      },
      [panelStates, strategy],
    );

    // No custom grid layout - let react-resizable-panels handle all sizing
    // We'll render fixed areas as regular divs and let the main content use PanelGroup

    // Render collapsible panels for multiple content entries (replaces tabs)
    const renderCollapsibleContentPanels = useCallback(
      (entries: { id: string; content: React.ReactNode; source?: string; priority: number }[]) => {
        if (!entries || entries.length === 0) {
          return children;
        }

        // For single entry, just render it directly
        if (entries.length === 1) {
          return entries[0].content;
        }

        // Multiple entries: create collapsible panels instead of tabs
        const totalEntries = entries.length;
        const baseSize = Math.max(100 / totalEntries, 15); // Ensure minimum 15% per panel
        const firstPanelSize = Math.min(baseSize * 1.5, 60); // First panel gets up to 60% but not more

        return (
          <StyledPanelGroup id={collapsibleGroupId.current} autoSaveId={`${autoSaveId}-collapsible`} direction="vertical" onLayout={handleLayoutChange}>
            {entries.map((entry, index) => {
              const isFirst = index === 0;
              const showHandle = index < entries.length - 1;
              const panelSize = isFirst ? firstPanelSize : baseSize;

              return (
                <React.Fragment key={entry.id}>
                  <Panel
                    id={`collapsible-${entry.id}`}
                    order={index + 1}
                    defaultSize={panelSize}
                    minSize={12} // Allow smaller collapse for more content
                    collapsible={true}
                    collapsedSize={6} // Very compact when collapsed for more space
                  >
                    <LayoutPanel variant="content" padding="compact" key={entry.id}>
                      <CollapsiblePanelHeader>
                        {entry.source ?? entry.id} {entry.priority > 0 && `(Priority: ${entry.priority})`}
                      </CollapsiblePanelHeader>
                      {entry.content}
                    </LayoutPanel>
                  </Panel>
                  {showHandle && <PanelResizeHandle id={`collapsible-handle-${index}`} />}
                </React.Fragment>
              );
            })}
          </StyledPanelGroup>
        );
      },
      [autoSaveId, children, handleLayoutChange],
    );

    // Determine resizable and fixed panels from visible areas
    const resizablePanels = inlineVisibleAreas.filter(
      (area) => finalPanelConfig[area]?.resizable !== false && (area === LayoutArea.LEFT_SIDEBAR || area === LayoutArea.RIGHT_SIDEBAR),
    );

    const fixedPanels = inlineVisibleAreas.filter(
      (area) => finalPanelConfig[area]?.resizable === false || area === LayoutArea.HEADER || area === LayoutArea.FOOTER,
    );

    // Render resizable content area with PanelGroup
    const renderResizableContent = () => {
      // Check if content area has panel content (like TabbedContent)
      const contentState = panelStates.get(LayoutArea.CONTENT);
      const hasContentPanelData = contentState?.content && contentState.isVisible;

      // Check if content state has multiple entries (from TabbedContent) - convert to collapsible panels
      const isTabbed =
        hasContentPanelData &&
        typeof contentState.content === 'object' &&
        contentState.content !== null &&
        'props' in contentState.content &&
        contentState.content.props?.entries?.length > 1;

      if (isTabbed && typeof contentState.content === 'object' && contentState.content !== null && 'props' in contentState.content) {
        // Use collapsible panels instead of tabs for multiple content entries
        return renderCollapsibleContentPanels(contentState.content.props.entries);
      }

      if (resizablePanels.length === 0) {
        // No resizable panels, but check if content area has data to show
        if (hasContentPanelData) {
          return renderPanelContent(LayoutArea.CONTENT);
        }
        return children;
      }

      return (
        <StyledPanelGroup
          id={mainGroupId.current}
          autoSaveId={autoSaveId}
          direction={direction}
          onLayout={(sizes) => {
            // Combine library callback with our intelligent sizing
            handleMainLayoutChange(sizes);
            // Call original onLayout callback if provided
            if (onLayout) {
              onLayout(sizes);
            }
            // Debug log for development
            if (process.env.NODE_ENV === 'development') {
              console.log('🎯 Panel Layout Updated:', { sizes, areas: inlineVisibleAreas });
            }
          }}
        >
          {resizablePanels.map((area, index) => {
            const config = finalPanelConfig[area];
            const state = panelStates.get(area);
            const shouldRender = state?.isVisible && isValidContent(state.content);

            if (!shouldRender) return null;

            const showHandle = index < resizablePanels.length - 1;

            return (
              <React.Fragment key={area}>
                <Panel
                  id={area}
                  order={index + 1}
                  defaultSize={(() => {
                    // Use intelligent sizing if available, fallback to config
                    const recommendedLayout = getMainRecommendedLayout();
                    return recommendedLayout[index] || constraintToNumber(config.defaultSize, viewport, direction, 20);
                  })()}
                  minSize={constraintToNumber(config.minSize, viewport, direction, 5)}
                  maxSize={constraintToNumber(config.maxSize, viewport, direction, 100)}
                  collapsible={config.collapsible}
                  collapsedSize={config.collapsible ? 3 : undefined} // Very compact when collapsed for better space utilization
                >
                  {renderPanelContent(area)}
                </Panel>
                {showHandle && <PanelResizeHandle id={`${area}-handle`} />}
              </React.Fragment>
            );
          })}

          {/* Content panel takes remaining space - render panel content if available, otherwise children */}
          <Panel id="main-content" order={resizablePanels.length + 1}>
            {hasContentPanelData && !isTabbed ? renderPanelContent(LayoutArea.CONTENT) : children}
          </Panel>
        </StyledPanelGroup>
      );
    };

    // Intelligent auto-layout system using strategy pattern detection
    const renderAutoLayout = () => {
      if (!autoLayout.structure) {
        return renderResizableContent();
      }

      // Development logging
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Rendering Layout Pattern:', autoLayout.pattern);
      }

      // Render based on auto-detected pattern
      return renderPatternLayout();
    };

    // Render layout based on detected pattern
    const renderPatternLayout = () => {
      const groups = autoLayout.getGroupsForPattern().sort((a, b) => a.order - b.order);

      if (groups.length === 0) return renderResizableContent();

      const primaryGroup = groups[0];
      const primaryDirection = autoLayout.structure?.primaryDirection || 'vertical';

      return (
        <StyledPanelGroup id={mainGroupId.current} autoSaveId={autoSaveId} direction={primaryDirection} onLayout={handleLayoutChange}>
          {groups.map((group, groupIndex) => renderLayoutGroup(group, groupIndex, groups.length))}
        </StyledPanelGroup>
      );
    };

    // Render individual layout group
    const renderLayoutGroup = (
      group: { id: string; isNested?: boolean; direction: string; panels: LayoutArea[]; order: number },
      groupIndex: number,
      totalGroups: number,
    ) => {
      const groupConfig = autoLayout.getPanelGroupConfig(group.id);
      if (!groupConfig) return null;

      if (group.isNested) {
        return renderNestedGroup(group, groupConfig, groupIndex);
      } else {
        return renderSimpleGroup(group, groupConfig, groupIndex, totalGroups);
      }
    };

    // Render nested panel group
    const renderNestedGroup = (
      group: { id: string; direction: string; panels: LayoutArea[] },
      groupConfig: { panels: LayoutArea[]; defaultSizes: number[]; direction: string },
      groupIndex: number,
    ) => (
      <Panel key={group.id} id={`group-${group.id}`} order={groupIndex + 1}>
        <StyledPanelGroup id={group.id} direction={group.direction as 'horizontal' | 'vertical'} onLayout={handleLayoutChange}>
          {groupConfig.panels.map((area: LayoutArea, panelIndex: number) =>
            renderPanelInGroup(area, panelIndex, groupConfig, group.direction as 'horizontal' | 'vertical'),
          )}
        </StyledPanelGroup>
      </Panel>
    );

    // Render simple panel group
    const renderSimpleGroup = (
      group: { panels: LayoutArea[] },
      groupConfig: { panels: LayoutArea[]; defaultSizes: number[] },
      groupIndex: number,
      totalGroups: number,
    ) => {
      const area = groupConfig.panels[0];
      const config = finalPanelConfig[area];
      const state = panelStates.get(area);
      const shouldRender = state?.isVisible && isValidContent(state.content);

      if (!shouldRender) return null;

      const showHandle = groupIndex < totalGroups - 1;
      const direction = autoLayout.structure?.primaryDirection || 'vertical';

      return (
        <React.Fragment key={area}>
          <Panel
            id={area}
            order={groupIndex + 1}
            defaultSize={constraintToNumber(config.defaultSize, viewport, direction, 20)}
            minSize={constraintToNumber(config.minSize, viewport, direction, 5)}
            maxSize={constraintToNumber(config.maxSize, viewport, direction, 50)}
            collapsible={config.collapsible}
            collapsedSize={config.collapsible ? 3 : undefined}
          >
            {renderPanelContent(area)}
          </Panel>
          {showHandle && <PanelResizeHandle id={`${area}-handle`} />}
        </React.Fragment>
      );
    };

    // Render panel within a group
    const renderPanelInGroup = (
      area: LayoutArea,
      panelIndex: number,
      groupConfig: { panels: LayoutArea[]; defaultSizes: number[] },
      direction: 'horizontal' | 'vertical',
    ) => {
      const config = finalPanelConfig[area];
      const state = panelStates.get(area);
      const shouldRender = state?.isVisible && isValidContent(state.content);

      if (!shouldRender) return null;

      const showHandle = panelIndex < groupConfig.panels.length - 1;
      const defaultSize =
        area === LayoutArea.CONTENT
          ? 100 // Content gets remaining space
          : groupConfig.defaultSizes[panelIndex] || constraintToNumber(config.defaultSize, viewport, direction, 25);

      return (
        <React.Fragment key={area}>
          <Panel
            id={area}
            order={panelIndex + 1}
            defaultSize={defaultSize}
            minSize={constraintToNumber(config.minSize, viewport, direction, 5)}
            maxSize={constraintToNumber(config.maxSize, viewport, direction, 100)}
            collapsible={config.collapsible}
            collapsedSize={config.collapsible ? 3 : undefined}
          >
            {area === LayoutArea.CONTENT ? renderResizableContent() : renderPanelContent(area)}
          </Panel>
          {showHandle && <PanelResizeHandle id={`${area}-handle`} />}
        </React.Fragment>
      );
    };

    // Main layout rendering with auto-detection
    return (
      <>
        <StyledResizableLayout className={className}>{renderAutoLayout()}</StyledResizableLayout>

        {/* Debug Panel - always render when visible, overlay on top */}
        <DebugPanel isVisible={isDebugPanelVisible} onClose={() => setIsDebugPanelVisible(false)} />

        {/* Debug Toggle Button - Ctrl+Shift+D or click to toggle */}
        {process.env.NODE_ENV === 'development' && (
          <DebugToggleButton onClick={() => setIsDebugPanelVisible((prev) => !prev)} title="Toggle Debug Panel (Ctrl+Shift+D)" aria-label="Toggle Debug Panel">
            🔧
          </DebugToggleButton>
        )}

        {/* Auto-layout debug info */}
        {process.env.NODE_ENV === 'development' && autoLayout.debugInfo && (
          <div
            style={{
              position: 'fixed',
              top: '10px',
              left: '10px',
              background: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '10px',
              borderRadius: '4px',
              fontSize: '12px',
              zIndex: 9999,
              fontFamily: 'monospace',
            }}
          >
            <div>Pattern: {autoLayout.pattern}</div>
            <div>Valid: {autoLayout.isLayoutValid() ? '✅' : '❌'}</div>
            <div>Total: {autoLayout.getTotalLayoutPercentage()}%</div>
            {autoLayout.getLayoutViolations().length > 0 && <div style={{ color: '#ff6b6b' }}>Violations: {autoLayout.getLayoutViolations().length}</div>}
          </div>
        )}
      </>
    );
  },
);

AutoPanelLayoutCore.displayName = 'AutoPanelLayoutCore';
